// Lookahead Poisson event scheduler. Emits engine-time StrikeIntent drafts
// (with _onsetEngineSec); the composer maps whenSec back to real audio time.
// Pure: randomness via injected Rng.

import type { DiatomDescriptor } from '@shared/diatom';
import type { ExcitationType, StrikeIntent } from '@shared/intents';
import type { Mood } from '@shared/parameterState';
import type { Rng } from '@shared/rng';
import { makeGaussian, nextRange, poissonInterval, weightedPick } from '@shared/rng';
import { clamp, clamp01 } from './smoothing';
import { MOOD_ARTICULATION } from './moods';
import { PitchField, wildProbability } from './pitchField';

export const ONSET_INTERVAL_MIN = 0.05;
export const ONSET_INTERVAL_MAX = 60;
const DECAY_SCALE: Record<Mood, number> = { abyssal: 2.5, tidepool: 0.8, storm: 0.4 };

/** A StrikeIntent plus the engine-time at which it should fire (mapped later). */
export interface IntentDraft extends StrikeIntent {
  _onsetEngineSec: number;
}

export interface SchedulerContext {
  mood: Mood;
  registerNorm: number;
  registerHz: number;
  densityOnsetsPerMin: number;
  densityNorm: number;
  tension: number;
  pureRough: number;
  pitchField: PitchField;
}

const PER_TICK_CAP = 64;

export class Scheduler {
  private readonly gaussian = makeGaussian();
  private nextOnsetEngineSec: number | null = null;
  readonly diatoms: readonly DiatomDescriptor[];
  private readonly lensId: string;
  /** Total onsets scheduled (incl. dropped/rejected) — for rate measurement. */
  scheduledOnsets = 0;
  /** Engine-time of the first scheduled onset — for rate measurement. */
  firstOnsetEngineSec: number | null = null;
  lastOnsetEngineSec = 0;
  /** Count of gesture-anchor (lens) onsets triggered — for frequency measurement. */
  gestureAnchors = 0;

  constructor(diatoms: readonly DiatomDescriptor[]) {
    if (diatoms.length === 0) throw new Error('Scheduler: no diatoms');
    this.diatoms = diatoms;
    const lens = diatoms.find((d) => d.backgroundLens);
    this.lensId = (lens ?? diatoms[diatoms.length - 1]).id;
  }

  /** Advance the onset queue across [engineTime, engineTime+aheadEngine]; emit due drafts. */
  advance(engineTimeSec: number, aheadEngineSec: number, ctx: SchedulerContext, rng: Rng): IntentDraft[] {
    const out: IntentDraft[] = [];
    let next =
      this.nextOnsetEngineSec ?? engineTimeSec + this.drawInterval(ctx.densityOnsetsPerMin, rng);
    const horizon = engineTimeSec + aheadEngineSec;
    let guard = 0;
    while (next <= horizon && guard < PER_TICK_CAP) {
      guard++;
      const onset = next;
      const art = MOOD_ARTICULATION[ctx.mood];

      // Abyssal silence window: insert a gap instead of an onset.
      if (art.silenceProb > 0 && rng() < art.silenceProb) {
        const gap = nextRange(rng, art.silenceLenSec[0], art.silenceLenSec[1]);
        next = onset + gap;
        continue;
      }

      this.scheduledOnsets++;
      if (this.firstOnsetEngineSec === null) this.firstOnsetEngineSec = onset;
      this.lastOnsetEngineSec = onset;
      const draft = this.buildIntent(onset, ctx, rng);
      if (draft) out.push(draft);

      next = onset + this.drawInterval(ctx.densityOnsetsPerMin, rng);
    }
    this.nextOnsetEngineSec = next;
    return out;
  }

  private drawInterval(onsetsPerMin: number, rng: Rng): number {
    const rate = onsetsPerMin / 60;
    const raw = poissonInterval(rng, rate);
    return clamp(raw, ONSET_INTERVAL_MIN, ONSET_INTERVAL_MAX);
  }

  private buildIntent(onsetEngineSec: number, ctx: SchedulerContext, rng: Rng): IntentDraft | null {
    const art = MOOD_ARTICULATION[ctx.mood];

    // Gesture anchor: abyssal, prob 0.08 -> strike the lens diatom, big + slow.
    const gesture = art.gestureAnchorProb > 0 && rng() < art.gestureAnchorProb;
    if (gesture) {
      this.gestureAnchors++;
      const energy = nextRange(rng, 0.8, 1);
      const durationHintSec = nextRange(rng, 20, 30);
      const pitchHz = clampPitch(ctx.registerHz * 0.5);
      const resolved = ctx.pitchField.resolve(pitchHz, false, onsetEngineSec, durationHintSec);
      const finalPitch = resolved ? resolved.hz : pitchHz;
      return {
        whenSec: 0,
        _onsetEngineSec: onsetEngineSec,
        diatomId: this.lensId,
        excitation: 'strike',
        energy,
        position: { x: rng(), y: rng() },
        pitchHz: finalPitch,
        snapped: resolved ? resolved.snapped : false,
        decayScale: 3.0,
        durationHintSec,
      };
    }

    // Diatom pick: register-matched (sizeRel inversely tracks pitch; abyssal favors large/low).
    const diatom = this.pickDiatom(ctx, rng);

    // Excitation
    const excitation = pickExcitation(ctx.mood, ctx.pureRough, rng);

    // Energy
    let mean = 0.4 + 0.3 * ctx.densityNorm;
    if (ctx.mood === 'abyssal') mean -= 0.15;
    mean *= 0.6 + 0.4 * (0.5 + 0.5 * ctx.tension);
    const energy = clamp01(mean + this.gaussian(rng) * 0.12);

    const decayScale = DECAY_SCALE[ctx.mood];
    const durationHintSec = clamp(0.5 + decayScale * 1.5, 0.2, 30);

    // Pitch: base from descriptor size + current register, then wild/snap gate.
    const baseHz = pitchForDiatom(diatom, ctx.registerHz);
    const wildP = wildProbability(art.wildProb, ctx.pureRough);
    const wild = rng() < wildP;
    const resolved = ctx.pitchField.resolve(baseHz, wild, onsetEngineSec, durationHintSec);
    if (resolved === null) return null; // harmonic gate rejected -> drop

    return {
      whenSec: 0,
      _onsetEngineSec: onsetEngineSec,
      diatomId: diatom.id,
      excitation,
      energy,
      position: { x: rng(), y: rng() },
      pitchHz: resolved.hz,
      snapped: resolved.snapped,
      decayScale,
      durationHintSec,
    };
  }

  private pickDiatom(ctx: SchedulerContext, rng: Rng): DiatomDescriptor {
    // weight by how well the diatom's implied register matches the current register.
    // small relSize -> higher pitch -> matches high registerNorm; large -> low.
    const weights = this.diatoms.map((d) => {
      // implied register-norm from relSize (relSize 0.2..1.6 -> high..low)
      const impliedHigh = clamp01((1.6 - d.relSize) / 1.4);
      let w = 1 - Math.abs(impliedHigh - ctx.registerNorm);
      w = 0.15 + 0.85 * Math.max(0, w);
      if (ctx.mood === 'abyssal') {
        // favor large/low bodies + the lens occasionally
        w *= 0.5 + d.relSize;
      } else if (ctx.mood === 'tidepool') {
        // favor small radial stars (higher symmetry, smaller size)
        w *= 0.5 + 0.5 * clamp01(d.symmetryN / 20) + (d.relSize < 0.9 ? 0.4 : 0);
      }
      // never auto-pick the lens for ordinary onsets (reserved for the gesture anchor)
      if (d.backgroundLens) w = 0;
      return w;
    });
    return weightedPick(rng, this.diatoms, weights);
  }
}

function pickExcitation(mood: Mood, pureRough: number, rng: Rng): ExcitationType {
  const w = MOOD_ARTICULATION[mood].excitation;
  const grain = w.grain + 0.3 * Math.max(0, pureRough);
  return weightedPick<ExcitationType>(rng, ['strike', 'grain'], [w.strike, grain]);
}

function pitchForDiatom(d: DiatomDescriptor, registerHz: number): number {
  // implied octave offset from relSize: small body -> up, large -> down (~ +/- 1 octave)
  const oct = (0.9 - d.relSize) * 1.2;
  return clampPitch(registerHz * Math.pow(2, oct));
}

function clampPitch(hz: number): number {
  return clamp(hz, 30, 4000);
}
